/**
 * Homophone sets that writers mix up most often, each with a one-line way to choose.
 *
 * Every set is a true homophone set in the CMU Pronouncing Dictionary's General
 * American pronunciations (ignoring stress) — `homophones.test.ts` checks each against
 * `public/data/cmu-homophones.txt`. Pairs that only sound close (affect/effect,
 * accept/except, advice/advise) are deliberately absent: they are not homophones, and
 * the page says so. The tips are written for this tool.
 */

export interface CommonHomophoneSet {
  words: string[];
  /** How to tell them apart, in a sentence. */
  tip: string;
}

export const COMMON_HOMOPHONES: readonly CommonHomophoneSet[] = [
  {
    words: ["their", "there", "they're"],
    tip: "Their shows ownership, there points to a place, and they're is they are.",
  },
  {
    words: ["to", "too", "two"],
    tip: "Two is the number; too means also or excessively; to is everything else.",
  },
  {
    words: ["its", "it's"],
    tip: "It's always stands for it is or it has; the possessive its has no apostrophe.",
  },
  {
    words: ["whose", "who's"],
    tip: "Who's is who is or who has; whose asks about ownership.",
  },
  {
    words: ["your", "you're"],
    tip: "You're is you are; your belongs to you.",
  },
  {
    words: ["hear", "here"],
    tip: "You hear with your ear; here is the opposite of there.",
  },
  {
    words: ["write", "right", "rite", "wright"],
    tip: "Write is to put words down; right is correct or the opposite of left; a rite is a ceremony.",
  },
  {
    words: ["weather", "whether"],
    tip: "Weather is the sky and the temperature; whether introduces a choice.",
  },
  {
    words: ["which", "witch"],
    tip: "Which asks you to choose; a witch casts spells.",
  },
  {
    words: ["principal", "principle"],
    tip: "A principal is a person or the main thing; a principle is a rule or belief.",
  },
  {
    words: ["stationary", "stationery"],
    tip: "Stationary means not moving; stationery is paper and pens (the e is for envelope).",
  },
  {
    words: ["bare", "bear"],
    tip: "Bare means uncovered; a bear is the animal, and to bear is to carry or endure.",
  },
  {
    words: ["break", "brake"],
    tip: "Break means to snap or pause; a brake slows a vehicle.",
  },
  {
    words: ["buy", "by", "bye"],
    tip: "Buy is to purchase; by is a preposition; bye ends a conversation.",
  },
  {
    words: ["cite", "sight", "site"],
    tip: "Cite a source; sight is vision; a site is a place or a website.",
  },
  {
    words: ["course", "coarse"],
    tip: "A course is a class or a route; coarse means rough.",
  },
  {
    words: ["dear", "deer"],
    tip: "Dear is a greeting or beloved; a deer is the animal.",
  },
  {
    words: ["flour", "flower"],
    tip: "Flour is ground grain; a flower blooms.",
  },
  {
    words: ["for", "four", "fore"],
    tip: "Four is the number; fore means at the front; for is the everyday preposition.",
  },
  {
    words: ["knew", "new", "gnu"],
    tip: "Knew is the past of know; new is not old; a gnu is an antelope.",
  },
  {
    words: ["know", "no"],
    tip: "Know means to be aware of; no is the negative.",
  },
  {
    words: ["mail", "male"],
    tip: "Mail is post; male is the opposite of female.",
  },
  {
    words: ["meat", "meet", "mete"],
    tip: "Meat is food; meet is to encounter; mete (out) is to deal out a punishment.",
  },
  {
    words: ["pair", "pare", "pear"],
    tip: "A pair is two of something; pare is to trim; a pear is a fruit.",
  },
  {
    words: ["peace", "piece"],
    tip: "Peace is calm; a piece is a part of a whole.",
  },
  {
    words: ["plain", "plane"],
    tip: "Plain means simple or a flat grassland; a plane flies, or is a flat surface.",
  },
  {
    words: ["rain", "reign", "rein"],
    tip: "Rain falls; a monarch's reign is their rule; a rein steers a horse.",
  },
  {
    words: ["role", "roll"],
    tip: "A role is a part you play; a roll is a turn, a list, or a bread.",
  },
  {
    words: ["sea", "see"],
    tip: "The sea is salt water; see is to look.",
  },
  {
    words: ["sole", "soul"],
    tip: "Sole means only or the bottom of a foot; the soul is the spirit.",
  },
  {
    words: ["some", "sum"],
    tip: "Some is an unspecified amount; a sum is a total.",
  },
  {
    words: ["son", "sun"],
    tip: "A son is a male child; the sun is the star.",
  },
  {
    words: ["steal", "steel"],
    tip: "Steal is to take what is not yours; steel is the metal.",
  },
  {
    words: ["tail", "tale"],
    tip: "A tail is at the back of an animal; a tale is a story.",
  },
  {
    words: ["threw", "through"],
    tip: "Threw is the past of throw; through means from one side to the other.",
  },
  {
    words: ["waist", "waste"],
    tip: "The waist is the middle of the body; waste is what is thrown away or squandered.",
  },
  {
    words: ["wait", "weight"],
    tip: "Wait means to stay until something happens; weight is how heavy something is.",
  },
  {
    words: ["way", "weigh", "whey"],
    tip: "A way is a route or a method; weigh is to measure heaviness; whey is a milk by-product.",
  },
  {
    words: ["weak", "week"],
    tip: "Weak is not strong; a week is seven days.",
  },
  {
    words: ["wear", "where", "ware"],
    tip: "Wear clothes; where asks about place; wares are goods for sale.",
  },
  {
    words: ["wood", "would"],
    tip: "Wood comes from trees; would is the modal verb.",
  },
  {
    words: ["air", "heir", "err"],
    tip: "Air is what we breathe; an heir inherits; to err is to make a mistake.",
  },
  {
    words: ["aloud", "allowed"],
    tip: "Aloud means out loud; allowed means permitted.",
  },
  {
    words: ["ate", "eight"],
    tip: "Ate is the past of eat; eight is the number.",
  },
  {
    words: ["board", "bored"],
    tip: "A board is a flat piece or a committee; bored means uninterested.",
  },
  {
    words: ["capital", "capitol"],
    tip: "Capital is a city, money, or an upper-case letter; the Capitol is a legislature's building.",
  },
  {
    words: ["cell", "sell"],
    tip: "A cell is the unit of life or a small room; sell is to trade for money.",
  },
  {
    words: ["cereal", "serial"],
    tip: "Cereal is grain or breakfast food; serial means arranged in a series.",
  },
  {
    words: ["die", "dye"],
    tip: "To die is to stop living; to dye is to colour.",
  },
  {
    words: ["fair", "fare"],
    tip: "Fair means just or light-coloured; a fare is what you pay to travel.",
  },
  {
    words: ["hole", "whole"],
    tip: "A hole is an opening; whole means complete.",
  },
  {
    words: ["hour", "our"],
    tip: "An hour is sixty minutes; our shows that something is ours.",
  },
  {
    words: ["idle", "idol"],
    tip: "Idle means doing nothing; an idol is someone or something worshipped.",
  },
  {
    words: ["lead", "led"],
    tip: "Led is the past of lead (to guide); the metal lead is also said led.",
  },
  {
    words: ["made", "maid"],
    tip: "Made is the past of make; a maid is a household worker.",
  },
  {
    words: ["main", "mane"],
    tip: "Main means chief; a mane is a lion's or horse's long hair.",
  },
  {
    words: ["morning", "mourning"],
    tip: "Morning is the start of the day; mourning is grieving.",
  },
  {
    words: ["none", "nun"],
    tip: "None means not any; a nun is a member of a religious order.",
  },
  {
    words: ["one", "won"],
    tip: "One is the number; won is the past of win.",
  },
  {
    words: ["pause", "paws"],
    tip: "A pause is a short stop; paws are animal feet.",
  },
  {
    words: ["prey", "pray"],
    tip: "Prey is hunted; to pray is to address a god.",
  },
  {
    words: ["profit", "prophet"],
    tip: "Profit is money gained; a prophet speaks for a god.",
  },
  {
    words: ["sail", "sale"],
    tip: "A sail catches wind; a sale is a transaction or a discount.",
  },
  {
    words: ["scene", "seen"],
    tip: "A scene is a view or part of a play; seen is the past participle of see.",
  },
  {
    words: ["sew", "so", "sow"],
    tip: "Sew with a needle; so links ideas; sow seeds (the animal sow rhymes with cow).",
  },
  {
    words: ["stair", "stare"],
    tip: "A stair is a step; to stare is to look hard.",
  },
  {
    words: ["suite", "sweet"],
    tip: "A suite is a set of rooms or pieces; sweet tastes of sugar.",
  },
  {
    words: ["symbol", "cymbal"],
    tip: "A symbol stands for something; a cymbal is a brass percussion plate.",
  },
  {
    words: ["vain", "vane", "vein"],
    tip: "Vain means conceited or futile; a vane shows the wind; a vein carries blood.",
  },
  {
    words: ["wail", "whale"],
    tip: "To wail is to cry loudly; a whale is a sea mammal.",
  },
];
